import { Alert, Button, Card, TextField } from '@safepos/ui';
import { formatRupiah } from '@safepos/shared';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePublicConfig } from '../lib/license';
import { useSubmit } from '../lib/useForm';

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <img
          src="/logo.png"
          alt="SafePOS"
          className="mx-auto h-20 w-auto max-w-full rounded-xl p-2"
          style={{ background: '#fff' }}
        />
        <Card title={title}>{children}</Card>
      </div>
    </main>
  );
}

export function LoginPage() {
  const cfg = usePublicConfig();
  const { status, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const s = useSubmit(async () => {
    try {
      await login(email, password, needTotp ? totp : undefined);
      nav('/office/dasbor', { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TOTP_REQUIRED') setNeedTotp(true);
      throw e;
    }
  });
  if (status === 'authenticated') return <Navigate to="/office/dasbor" replace />;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void s.run();
  };
  return (
    <Shell title="Masuk ke Back Office">
      <form onSubmit={submit} className="space-y-4">
        {s.error && !(needTotp && !totp) && <Alert>{s.error}</Alert>}
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {needTotp && (
          <TextField
            label="Kode autentikator (6 digit)"
            inputMode="numeric"
            maxLength={6}
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            autoFocus
            required
          />
        )}
        <Button type="submit" disabled={s.busy} className="w-full">
          {s.busy ? 'Memproses…' : 'Masuk'}
        </Button>
        <div className="flex justify-between text-sm">
          <Link to="/lupa-password" className="text-brand-700">
            Lupa password?
          </Link>
          {cfg.data?.registrationEnabled !== false && (
            <Link to="/daftar" className="text-brand-700">
              Daftar bisnis baru
            </Link>
          )}
        </div>
      </form>
    </Shell>
  );
}

interface PublicPlans {
  trialDays: number;
  plans: { code: string; name: string; priceMonthly: number }[];
}

export function RegisterPage() {
  const { status, register } = useAuth();
  const nav = useNavigate();
  // ?paket=growth dari situs pemasaran memilih paket di awal
  const [params] = useSearchParams();
  const [planCode, setPlanCode] = useState(params.get('paket') ?? '');
  const plans = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get<PublicPlans>('/public/plans'),
  });
  const chosen = plans.data?.plans.find((p) => p.code === planCode);
  const [f, setF] = useState({
    businessName: '',
    businessType: 'fnb',
    ownerName: '',
    email: '',
    password: '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value });
  const s = useSubmit(async () => {
    await register({ ...f, ...(chosen ? { planCode: chosen.code } : {}) });
    nav('/office/dasbor', { replace: true });
  });
  if (status === 'authenticated') return <Navigate to="/office/dasbor" replace />;
  return (
    <Shell
      title={
        chosen && chosen.priceMonthly === 0
          ? `Daftar bisnis baru (paket ${chosen.name} gratis)`
          : `Daftar bisnis baru (uji coba ${plans.data?.trialDays ?? 14} hari)`
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void s.run();
        }}
        className="space-y-4"
      >
        {s.error && <Alert>{s.error}</Alert>}
        {plans.data && (
          <label className="block space-y-1 text-sm font-medium text-slate-700">
            Paket
            <select
              className="w-full rounded-lg border border-slate-300 bg-surface px-3 py-2"
              value={chosen?.code ?? ''}
              onChange={(e) => setPlanCode(e.target.value)}
            >
              <option value="">Paket uji coba bawaan</option>
              {plans.data.plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name} —{' '}
                  {p.priceMonthly === 0 ? 'Gratis' : `${formatRupiah(p.priceMonthly)}/bulan`}
                </option>
              ))}
            </select>
            <span className="block text-xs font-normal text-slate-500">
              Paket berbayar dimulai dengan uji coba gratis; tidak ada penagihan otomatis selama uji
              coba.
            </span>
          </label>
        )}
        <TextField
          label="Nama bisnis"
          value={f.businessName}
          onChange={set('businessName')}
          required
        />
        <label className="block space-y-1 text-sm font-medium text-slate-700">
          Jenis usaha
          <select
            className="w-full rounded-lg border border-slate-300 bg-surface px-3 py-2"
            value={f.businessType}
            onChange={set('businessType')}
          >
            <option value="fnb">F&B (kafe/restoran)</option>
            <option value="retail">Retail (toko)</option>
            <option value="both">Keduanya</option>
          </select>
        </label>
        <TextField label="Nama pemilik" value={f.ownerName} onChange={set('ownerName')} required />
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={f.email}
          onChange={set('email')}
          required
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="Minimal 10 karakter, kombinasi huruf dan angka"
          value={f.password}
          onChange={set('password')}
          required
        />
        <Button type="submit" disabled={s.busy} className="w-full">
          {s.busy ? 'Memproses…' : 'Daftar'}
        </Button>
        <p className="text-center text-sm">
          <Link to="/masuk" className="text-brand-700">
            Sudah punya akun? Masuk
          </Link>
        </p>
      </form>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const s = useSubmit(async () => {
    if (token) {
      await api.post('/auth/password/reset', { token, password });
      setDone('Password berhasil diubah. Silakan masuk.');
    } else {
      await api.post('/auth/password/forgot', { email });
      setDone('Jika email terdaftar, instruksi pengaturan ulang telah dikirim.');
    }
  });
  return (
    <Shell title={token ? 'Atur password baru' : 'Lupa password'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void s.run();
        }}
        className="space-y-4"
      >
        {s.error && <Alert>{s.error}</Alert>}
        {done && <Alert tone="success">{done}</Alert>}
        {token ? (
          <TextField
            label="Password baru"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        ) : (
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        )}
        <Button type="submit" disabled={s.busy} className="w-full">
          {token ? 'Simpan password' : 'Kirim instruksi'}
        </Button>
        <p className="text-center text-sm">
          <Link to="/masuk" className="text-brand-700">
            Kembali ke halaman masuk
          </Link>
        </p>
      </form>
    </Shell>
  );
}
